import * as React from "react";
import * as Sentry from "@sentry/react";

import { Link, useLocation } from "react-router-dom";
import {
  selectIsLoggedIn,
  selectShowPayorMembers,
  selectUserInfo,
  selectUserRole,
} from "../../../src/redux/app";

import { DepartmentSwitcher } from "./DepartmentSwitcher";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NewNavBar } from "./landing_pages/navigation/NewNavBar";
import { PartnerSwitcher } from "./hand_off_partner_portal/components/PartnerSwitcher";
import PortalSwitcher from "./PortalSwitcher";
import { ProviderAccountDropdown } from "./NavMenus";
import { StandardButton } from "@verse_core/shared_components/buttons/StandardButton";
import { cn } from "@verse_core/shared_components/ClassName";
import { datadogRum } from "@datadog/browser-rum";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { getPortalLandingPath } from "./util/portalRoutes";
import { identifyUser } from "../useAnalytics";
import { rebrandEnabled } from "./util/brandingFlag";
import { useAppSelector } from "../../../src/redux/hooks";
import { useAuth0 } from "@auth0/auth0-react";

const MobileNavLink = (props: {
  href?: string;
  text: string;
  className?: string;
  onClick?: () => void;
}) => {
  return (
    <a href={props.href} className="tw-no-underline" onClick={props.onClick}>
      <li
        className={`tw-list-none tw-px-[18px] tw-pb-[13px] tw-pt-[15px] tw-font-dm-sans tw-text-[16px] tw-text-black hover:tw-bg-light-grey ${props.className}`}
      >
        {props.text}
      </li>
    </a>
  );
};

const DesktopNavLink = (props: {
  href: string;
  text: string;
  className?: string;
}) => {
  const location = useLocation();

  return (
    <div className="tw-flex tw-flex-col tw-items-center">
      <Link
        to={props.href}
        className={cn(
          "tw-font-500 tw-flex tw-h-full tw-items-center tw-justify-center tw-px-[15px] tw-align-middle tw-text-[16px] tw-no-underline hover:tw-bg-gray-light-4",
          rebrandEnabled() ? "tw-text-verse-text-secondary" : "tw-text-black",
          props.className,
        )}
      >
        {props.text}
      </Link>
      {location.pathname === props.href && (
        <div
          className={cn(
            "-tw-mt-[4px] tw-h-[4px] tw-w-[80%]",
            rebrandEnabled() ? "tw-bg-verse-text-brand" : "tw-bg-blue-1",
          )}
        ></div>
      )}
    </div>
  );
};

const RoleSpecificLinks = (props: { isMobile: boolean; role: string }) => {
  const showMembers = useAppSelector(selectShowPayorMembers);

  switch (props.role) {
    case "provider":
      if (props.isMobile) {
        return (
          <>
            <MobileNavLink
              href="/provider_order_list/index"
              text="Patient Orders"
            />
            <MobileNavLink href="/provider_orders/new" text="Place New Order" />
            {showMembers && <MobileNavLink href="/members" text="Members" />}
          </>
        );
      } else {
        return (
          <>
            <DesktopNavLink
              href="/provider_order_list/index"
              text="Patient Orders"
            />
            <DesktopNavLink
              href="/provider_orders/new"
              text="Place New Order"
            />
            {showMembers && <DesktopNavLink href="/members" text="Members" />}
          </>
        );
      }
    case "manufacturer":
      if (props.isMobile) {
        return (
          <MobileNavLink
            href="/manufacturer_order_list/index"
            text="Dashboard"
          />
        );
      } else {
        return (
          <DesktopNavLink
            href="/manufacturer_order_list/index"
            text="Dashboard"
          />
        );
      }
    case "hand_off_partner":
      if (props.isMobile) {
        return (
          <MobileNavLink
            href="/hand_off_partner_order_list/index"
            text="Patient Orders"
          />
        );
      } else {
        return (
          <DesktopNavLink
            href="/hand_off_partner_order_list/index"
            text="Patient Orders"
          />
        );
      }
    default:
      return null;
  }
};

const ProviderNavigation = ({
  isDark,
  showOldNav,
}: {
  isDark?: boolean;
  showOldNav?: boolean;
}) => {
  const [mobileMenuVisible, setMobileMenuVisible] = React.useState(false);

  const [expanded, setExpanded] = React.useState(false);
  const url = new URL(window.location.href);
  const isJetLenses = url.pathname.substring(1) === "/jetlenses";

  const currentUserInfo = useAppSelector(selectUserInfo);
  const user = currentUserInfo.userSettings.name || currentUserInfo.email;
  const loggedIn = useAppSelector(selectIsLoggedIn);
  const activeRole = useAppSelector(selectUserRole);

  const { isAuthenticated, logout } = useAuth0();

  React.useEffect(() => {
    if (!window.location.host.includes("localhost")) {
      if (currentUserInfo.id && user) {
        // Datadog RUM
        datadogRum.setUser({
          id: currentUserInfo.id.toString(),
          email: currentUserInfo.email,
          name: user,
        });

        // Rudderstack
        identifyUser(currentUserInfo.id, {
          name: user,
          email: currentUserInfo.email,
        });

        // Sentry
        Sentry.setUser({
          id: currentUserInfo.id.toString(), // Internal identifier for the user
          username: user, // Display name for the user
          email: currentUserInfo.email, // User's email address
          // Add any other custom user data here
          internal_user: currentUserInfo.email.endsWith("@versemedical.com"),
        });
      }
    }
  }, [currentUserInfo]);

  if (isJetLenses) {
    return null;
  }

  if (!loggedIn && !showOldNav) {
    return (
      <div
        className={cn(
          "tw-flex tw-h-[72px] tw-flex-row tw-items-center tw-gap-x-[48px] tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-px-[24px]",
          isDark
            ? "tw-border-b-white tw-border-opacity-25"
            : "tw-border-b-gray-light-1",
        )}
      >
        <Link to="/">
          <div
            className={cn(
              "tw-h-[50px] tw-w-[160px] tw-bg-contain tw-bg-center tw-bg-no-repeat",
              isDark
                ? "tw-bg-[url('/homepage/logoDark.png')]"
                : "tw-bg-[url('/homepage/logo.png')]",
            )}
          />
        </Link>
        <NewNavBar isDark={isDark ?? false} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "tw-relative tw-z-50 tw-h-[80px] tw-border-0 tw-border-b-[0.5px] tw-border-solid tw-bg-white",
        rebrandEnabled()
          ? "tw-border-b-verse-border-primary"
          : "tw-border-b-gray-light-1",
      )}
    >
      {/* Displays mobile menu overlay if mobile menu toggled on */}
      {mobileMenuVisible && (
        <>
          <div
            className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-10 tw-bg-gray-500 tw-bg-opacity-90"
            onClick={() => setMobileMenuVisible(false)}
          />
          <div className="tw-fixed tw-bottom-0 tw-right-0 tw-top-0 tw-z-20 tw-w-[300px] tw-bg-white">
            <div className="tw-relative">
              <div className="tw-p-[18px]">
                <i
                  className="fas fa-xmark tw-float-right tw-cursor-pointer tw-text-[24px] tw-text-gray-3"
                  onClick={() => setMobileMenuVisible(false)}
                ></i>
              </div>
              <ul className="tw-mt-[40px] tw-pl-0">
                {loggedIn ? (
                  <>
                    <RoleSpecificLinks isMobile={true} role={activeRole} />
                    <MobileNavLink href="/contact_us" text="Contact Us" />
                    <div
                      onMouseEnter={() => setExpanded(true)}
                      onMouseLeave={() => setExpanded(false)}
                    >
                      <li
                        className={`tw-list-none ${
                          expanded ? "tw-bg-light-grey" : "tw-bg-white"
                        } tw-px-[18px] tw-pb-[13px] tw-pt-[15px] tw-font-dm-sans tw-text-[16px] tw-text-black tw-no-underline hover:tw-bg-light-grey`}
                      >
                        <i className="fas fa-circle-user tw-align-top tw-text-[24px] tw-text-gray-3"></i>
                        <span className="tw-mx-[10px] tw-align-super">
                          {user}
                        </span>
                        <i
                          className={`fas ${
                            expanded ? "fa-chevron-up" : "fa-chevron-down"
                          } tw-float-right tw-mt-[5px] tw-align-super tw-text-[16px] tw-text-gray-3`}
                        ></i>
                      </li>
                      {expanded && (
                        <>
                          {activeRole === "provider" && (
                            <MobileNavLink
                              href="/provider-settings"
                              text="Account Settings"
                              className="tw-bg-blue-light-1"
                            />
                          )}
                          <MobileNavLink
                            href={
                              isAuthenticated ? undefined : "/graphql/logout"
                            }
                            text="Log Out"
                            className="tw-bg-blue-light-1"
                            onClick={() => {
                              datadogRum.stopSession();
                              if (isAuthenticated) {
                                logout({
                                  logoutParams: {
                                    returnTo: window.location.origin,
                                  },
                                });
                              }
                            }}
                          />
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <MobileNavLink href="/payors" text="For Payors" />
                    <MobileNavLink href="/about_us" text="About Us" />
                    <MobileNavLink href="/contact_us" text="Contact Us" />
                    <MobileNavLink
                      href="/password-login?type=provider"
                      text="Log In"
                    />
                  </>
                )}
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Logo, visible on both desktop and mobile */}
      <div className="tw-mx-auto">
        {/* Menu activation button, only shows up on smaller screens */}
        <button
          id=""
          className="tw-float-right tw-hidden tw-border-none tw-bg-white tw-p-[23px] tw-font-dm-sans tw-text-[23px] tw-text-gray-3 1040:tw-block"
          type="button"
          onClick={() => setMobileMenuVisible(true)}
        >
          <FontAwesomeIcon icon={faBars} className="tw-text-[26px]" />
        </button>
        <div
          className={cn(
            "tw-float-left",
            // 26px centres the design's 28px lockup in our 80px bar.
            rebrandEnabled()
              ? "tw-py-[26px] tw-pl-[24px]"
              : "tw-p-[18px] tw-pb-0 tw-pr-0",
          )}
        >
          <figure
            id="versemedical-logo"
            style={
              rebrandEnabled()
                ? { minWidth: "min-content" }
                : { marginTop: "-10px", minWidth: "min-content" }
            }
          >
            <Link to={loggedIn ? getPortalLandingPath(activeRole) : "/"}>
              {rebrandEnabled() ? (
                <img
                  src="/rebranded/verse-lockup-dark.png"
                  alt="Verse Medical"
                  className="tw-h-[28px] tw-w-[120px]"
                />
              ) : (
                <img src="/alternate_logo.svg" height="59" />
              )}
            </Link>
          </figure>
        </div>

        {/* Desktop menu */}
        <div className="tw-float-right tw-flex tw-h-[80px] tw-flex-row tw-items-stretch tw-justify-items-end tw-px-[15px] 1040:tw-hidden">
          {loggedIn ? (
            <>
              <RoleSpecificLinks isMobile={false} role={activeRole} />
              <DesktopNavLink href="/contact_us" text="Contact Us" />
              <div className="tw-group tw-relative tw-py-[15px]">
                <button
                  data-testid="account-button"
                  className={cn(
                    "tw-font-500 tw-ml-[20px] tw-rounded-md tw-border-[1px] tw-border-solid tw-bg-white tw-pb-[5px] tw-pl-[12px] tw-pr-[15px] tw-pt-[10px] tw-text-[16px] tw-no-underline",
                    rebrandEnabled()
                      ? "tw-border-gray-light-1 tw-text-gray-0"
                      : "tw-border-gray-5 tw-text-black",
                  )}
                >
                  <i
                    className={cn(
                      "fas fa-circle-user tw-align-top tw-text-[24px]",
                      rebrandEnabled() ? "tw-text-gray-4" : "tw-text-black",
                    )}
                  ></i>
                  <span className="tw-mx-[10px] tw-align-super">{user}</span>
                  <i
                    className={cn(
                      "fas fa-chevron-down tw-align-super tw-text-[16px]",
                      rebrandEnabled() ? "tw-text-gray-4" : "tw-text-black",
                    )}
                  ></i>
                </button>
                <ProviderAccountDropdown role={activeRole} />
              </div>

              {activeRole === "hand_off_partner" ? (
                <PartnerSwitcher loggedIn={loggedIn} />
              ) : (
                <DepartmentSwitcher loggedIn={loggedIn} />
              )}
              <PortalSwitcher loggedIn={loggedIn} />
            </>
          ) : (
            <>
              <DesktopNavLink href="/payors" text="For Payors" />
              <DesktopNavLink href="/about_us" text="About Us" />
              <DesktopNavLink href="/contact_us" text="Contact Us" />
              <div className="tw-ml-[15px] tw-flex tw-items-center">
                <Link to="/password-login?type=provider">
                  <StandardButton
                    style="primary-blue"
                    className="tw-w-[80px] tw-text-[16px]"
                    onClick={() => {}}
                  >
                    Log in
                  </StandardButton>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderNavigation;
